import React, { useEffect, useState } from 'react';
import client from '../../../api/client';
import Toggle from '../../../components/Toggle';

export default function WorkflowSettings() {
  const [esc, setEsc] = useState({ hours_until_at_risk: 24, hours_until_critical: 48, is_active: true });
  const [escSaving, setEscSaving] = useState(false);
  const [escSaved, setEscSaved] = useState(false);
  const [transitions, setTransitions] = useState([]);

  useEffect(() => {
    client.get('/config/escalation/').then(r => setEsc(r.data)).catch(() => {});
    client.get('/config/mandatory-fields/').then(r => setTransitions(r.data)).catch(() => {});
  }, []);

  const saveEsc = async () => {
    setEscSaving(true);
    await client.patch('/config/escalation/', esc);
    setEscSaving(false);
    setEscSaved(true);
    setTimeout(() => setEscSaved(false), 2000);
  };

  const toggleField = async (fromS, toS, fieldName, fieldLabel, current) => {
    const newVal = !current;
    setTransitions(ts => ts.map(t =>
      t.from_status === fromS && t.to_status === toS
        ? { ...t, fields: t.fields.map(f => f.field_name === fieldName ? { ...f, is_required: newVal } : f) }
        : t
    ));
    await client.patch('/config/mandatory-fields/update/', {
      from_status: fromS,
      to_status: toS,
      field_name: fieldName,
      field_label: fieldLabel,
      is_required: newVal,
    });
  };

  return (
    <div className="space-y-6">
      {/* Escalation */}
      <div className="card p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-bold text-brand-500 text-sm">Auto-Escalation</h2>
            <p className="text-xs text-gray-400 mt-0.5">Automatically flag open tickets based on how long they've been open</p>
          </div>
          <Toggle enabled={esc.is_active} onToggle={() => setEsc(e => ({ ...e, is_active: !e.is_active }))} />
        </div>
        <div className={`flex gap-6 items-end flex-wrap ${!esc.is_active ? 'opacity-40 pointer-events-none' : ''}`}>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">At Risk after (hours)</label>
            <input
              type="number"
              min={1}
              value={esc.hours_until_at_risk}
              onChange={e => setEsc(x => ({ ...x, hours_until_at_risk: +e.target.value }))}
              className="input-field w-28"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Critical after (hours)</label>
            <input
              type="number"
              min={1}
              value={esc.hours_until_critical}
              onChange={e => setEsc(x => ({ ...x, hours_until_critical: +e.target.value }))}
              className="input-field w-28"
            />
          </div>
          <button
            onClick={saveEsc}
            disabled={escSaving}
            className="px-4 py-2 bg-brand-500 text-white text-sm font-semibold rounded-xl hover:bg-brand-600 transition-colors disabled:opacity-50"
          >
            {escSaved ? '✓ Saved' : escSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {/* Mandatory Fields */}
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-brand-50">
          <h2 className="font-bold text-brand-500 text-sm">Mandatory Fields per Transition</h2>
          <p className="text-xs text-gray-400 mt-0.5">These fields must be filled before a ticket can be moved to the next status</p>
        </div>
        <div className="divide-y divide-brand-50">
          {transitions.filter(t => t.fields.length > 0).map(t => (
            <div key={`${t.from_status}-${t.to_status}`} className="px-6 py-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm font-semibold text-gray-700">{t.from_status}</span>
                <span className="text-gray-300">→</span>
                <span className="text-sm font-semibold text-brand-600">{t.to_status}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {t.fields.map(f => (
                  <div
                    key={f.field_name}
                    onClick={() => toggleField(t.from_status, t.to_status, f.field_name, f.field_label, f.is_required)}
                    className="flex items-center gap-2.5 cursor-pointer group"
                  >
                    <Toggle size="sm" enabled={f.is_required} onToggle={() => toggleField(t.from_status, t.to_status, f.field_name, f.field_label, f.is_required)} />
                    <span className={`text-xs select-none ${f.is_required ? 'text-brand-600 font-semibold' : 'text-gray-500'}`}>
                      {f.field_label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
